import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Opportunity as ApiOpportunity } from '../../../../../js/api/models/Opportunity';
import { AssignmentsService } from '../../../../../js/api/services/AssignmentsService';
import { useDocumentTitle } from '../../../../../js/app/hooks/use-document-title';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { Button } from '../../../components/button';
import { PageHeader } from '../../../components/PageHeader';
import { useCodeDefinitionsAsMaps } from '../../../hooks/useCodeDefinitions';
import { useDebounce } from '../../../hooks/useDebounce';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import OpportunityCard from './OpportunityCard';
import OpportunityFilters from './OpportunityFilters';

const OpportunityList: React.FC = () => {
  useDocumentTitle('Opportunities');
  const [opportunities, setOpportunities] = useState<ApiOpportunity[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const { data: codeDefinitionMaps } = useCodeDefinitionsAsMaps([
    'Degree',
    'Specialty',
    'ShiftRateTypes',
    'ShiftRateTypeIcons',
  ]);

  // Filter state
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('-');
  const [showClosed, setShowClosed] = useState(false);

  // Pagination state
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 12; // Number of items per page

  // Debounce search to avoid too many API calls
  const debouncedSearch = useDebounce(search, 300);
  const navigate = useNavigate();

  const fetchInitialOpportunities = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    try {
      const ordering = sortDirection && sortBy ? `${sortDirection}${sortBy}` : sortBy;
      const response = await AssignmentsService.assignmentsOpportunitiesList({
        search: debouncedSearch || undefined,
        ordering: ordering || undefined,
        showClosed: showClosed,
        limit: PAGE_SIZE,
        offset: 0,
      });

      const newOpportunities = response.results || [];
      setOpportunities(newOpportunities);
      setTotalCount(response.count || 0);
      setHasMore(response.next !== null);
      setOffset(newOpportunities.length);
    } catch (error: unknown) {
      handleApiError(error, {
        messagePrefix: 'Error fetching opportunities',
      });
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, sortBy, sortDirection, showClosed, PAGE_SIZE]);

  const loadMoreOpportunities = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const ordering = sortDirection && sortBy ? `${sortDirection}${sortBy}` : sortBy;
      const response = await AssignmentsService.assignmentsOpportunitiesList({
        search: debouncedSearch || undefined,
        ordering: ordering || undefined,
        showClosed: showClosed,
        limit: PAGE_SIZE,
        offset: offset,
      });

      const newOpportunities = response.results || [];
      setOpportunities((prev) => {
        const existingIds = new Set(prev.map((opp) => opp.id));
        const uniqueNew = newOpportunities.filter((opp) => !existingIds.has(opp.id));
        return [...prev, ...uniqueNew];
      });

      setHasMore(response.next !== null);
      setOffset((prevOffset) => prevOffset + PAGE_SIZE);
    } catch (error: unknown) {
      handleApiError(error, {
        messagePrefix: 'Error fetching opportunities',
      });
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, offset, debouncedSearch, sortBy, sortDirection, showClosed, PAGE_SIZE]);

  const { loadingRef } = useInfiniteScroll(loadMoreOpportunities, hasMore, {
    enabled: hasMore && !loading,
  });

  React.useEffect(() => {
    fetchInitialOpportunities();
  }, [fetchInitialOpportunities]);

  const handleResetList = () => {
    setSearch('');
    setSortBy('created_at');
    setSortDirection('-');
    setShowClosed(false);
  };

  const handleCreateOpportunity = () => {
    navigate('/app/organization/opportunities/new');
  };

  return (
    <>
      <PageHeader
        backUrl="/app"
        title="Opportunities"
        buttonHtml={
          <>
            <Button color="zinc-outline" onClick={handleResetList}>
              Reset List
            </Button>
            <Button color="brand-green-outline-on-dark" onClick={handleCreateOpportunity}>
              Create Opportunity
            </Button>
          </>
        }
      />

      <main className="tw:max-w-7xl tw:mx-auto tw:mt-3 tw:px-6">
        <div className="tw:flex tw:flex-col tw:gap-3">
          <OpportunityFilters
            search={search}
            onSearchChange={setSearch}
            sortBy={sortBy}
            onSortByChange={setSortBy}
            sortDirection={sortDirection}
            onSortDirectionChange={setSortDirection}
            showClosed={showClosed}
            onShowClosedChange={setShowClosed}
          />
        </div>
        <div className="tw:mt-3">
          {loading && opportunities.length === 0 && (
            <div className="tw:text-center tw:py-8">Loading opportunities...</div>
          )}
          {!loading && opportunities.length === 0 ? (
            <div className="tw-col-span-full tw:text-center tw:text-zinc-500 tw:py-8">
              {search ? 'No opportunities found matching your search.' : 'No opportunities found.'}
            </div>
          ) : (
            <div className="tw:grid tw:grid-cols-1 tw:sm:grid-cols-2 tw:md:grid-cols-3 tw:gap-3">
              {opportunities.map((opportunity) => (
                <OpportunityCard
                  key={opportunity.id}
                  opportunity={opportunity}
                  degreeMap={codeDefinitionMaps?.Degree || {}}
                  specialtyMap={codeDefinitionMaps?.Specialty || {}}
                  shiftRateTypeMap={codeDefinitionMaps?.ShiftRateTypes || {}}
                  shiftRateTypeIconMap={codeDefinitionMaps?.ShiftRateTypeIcons || {}}
                />
              ))}
            </div>
          )}

          {/* Infinite scroll loading indicator */}
          {hasMore && (
            <div ref={loadingRef} className="tw:flex tw:justify-center tw:py-8">
              {loadingMore && (
                <div className="tw:text-center tw:text-zinc-500">Loading more opportunities...</div>
              )}
            </div>
          )}

          {/* Show total count when there are results */}
          {opportunities.length > 0 && (
            <div className="tw:text-center tw:text-zinc-500 tw:mt-4">
              Showing {opportunities.length} of {totalCount} opportunities
            </div>
          )}
        </div>
      </main>
    </>
  );
};

export default OpportunityList;
